
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { gql, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import LayoutWrapper from './LayoutWrapper';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect'
import { InputNumber } from 'primereact/inputnumber';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import AdpositionInput from "./common/AdpositionInput";
import AdsizeAutocomplete from "./common/AdsizeAutocomplete";
import { InputSwitch } from "primereact/inputswitch";
import Cta from "./common/marketplace/CTA";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { ADSIZE_SUGGESTIONS } from "../constants";
import { MULTI_FIELD_READ_MUTATION } from "./adapters/utils/constants";
import { MULTI_FIELD_UPDATE_MUTATION } from "../mutations/mutations";
import { pushMessage } from "../reducers/messagingReducer";

/**
 * @typedef {Object} VideoAdPositionConfig
 * @property {string} network_id - The network identifier, typically including publisher and ad unit path (e.g. "/22759405143/Ebony.com/EBONY_Videos_640x480").
 * @property {string} playerSize - The dimensions of the video player as a stringified array (e.g. "[640, 480]").
 * @property {string} context - The ad context; usually "instream" for video ads played within content.
 * @property {number} linearity - Indicates whether the ad is linear (1) or non-linear (2).
 * @property {number} max_duration - Maximum allowed ad duration in seconds.
 * @property {number} min_duration - Minimum allowed ad duration in seconds.
 * @property {number} skip_after - Number of seconds after which the ad becomes skippable.
 * @property {boolean} skip - Whether the ad is skippable.
 * @property {number} placement - The placement type (1 for in-stream, 2 for out-stream, etc.).
 * @property {number} playback_method - The playback method (e.g. 6 = auto-play sound on).
 * @property {number} delivery - The delivery type (e.g. 1 = progressive, 2 = streaming).
 * @property {number} plcmt - Additional placement classification, often matching IAB standards.
 */


const VIDEO_INPUTS = {
    network_id: {
        label: 'Video Network ID',
        component: InputText,
        required: true
    },
    playerSize: {
        label: 'Player Size',
        component: Dropdown,
        skipRender: true
    },
    context: {
        label: 'Context',
        component: Dropdown,
        options: [
            { label: 'Instream', value: 'instream' },
            { label: 'Outstream', value: 'outstream' },
        ],
        defaultValue: 1
    },
    linearity: {
        label: 'Linearity',
        component: Dropdown,
        options: [
            { label: "Linear / In-Stream", value: "instream" },
            { label: "Non-Linear / Overlay", value: "overlay" }
        ],
        defaultValue: "instream"
    },
    max_duration: {
        label: 'Max Duration',
        component: InputNumber,
        skipRender: false
    },
    min_duration: {
        label: 'Min Duration',
        component: InputNumber,
        skipRender: false
    },
    skip_after: {
        label: 'Skip After (seconds)',
        component: InputNumber,
        skipRender: false
    },
    skip: {
        label: 'Skip',
        component: InputSwitch,
        skipRender: false
    },
    placement: {
        label: 'Placement',
        component: Dropdown,
        skipRender: false,
        options: [
            {label: "1 (In-Stream)", value: 1},
            {label: "2 (In-Banner)", value: 2},
            {label: "3 (In-Article)", value: 3},
            {label: "4 (In-Feed)", value: 4},
            {label: "5 (Interstitial)", value: 5}
        ]
    },
    playback_method: {
        label: 'Playback Method',
        component: Dropdown,
        options: [
            { label: "Initiates on Page Load with Sound On", value: 1 },
            { label: "Initiates on Page Load with Sound Off by Default", value: 2 },
            { label: "Initiates on Click with Sound On", value: 3 },
            { label: "Initiates on Mouse-Over with Sound On", value: 4 },
            { label: "Initiates on Entering Viewport with Sound On", value: 5 },
            { label: "Initiates on Entering Viewport with Sound Off by Default", value: 6 }
        ],
        default: 6
    },
    delivery: {
        label: 'Delivery',
        component: MultiSelect,
        skipRender: false,
        multiselect: true,
        default: 1,
        options: [
            {label: '1 - Streaming', value: 1},
            {label: '2 - Progressive', value: 2},
            {label: '3 - Download', value: 3}
        ]
    },
    plcmt: {
        label: 'plcmt',
        component: Dropdown,
        skipRender: false,
        options: [
            {label: '1 - Instream', value: 1},
            {label: '2 - Accompanying Content', value: 2},
            {label: '3 - Interstitial', value: 3},
            {label: '4 - No Content/Standalone', value: 4},
        ]
    }
}

const DEFAULT_VIDEO_FIELDS = {
    "network_id": "",
    "playerSize": "[640, 480]",
    "context": 'instream',
    "linearity": "instream",
    "max_duration": 60,
    "min_duration": 1,
    "skip_after": 15,
    "skip": true,
    "placement": 1,
    "playback_method": 6,
    "delivery": [1],
    "plcmt": 1
}

const InputSection = ({ children, sectionName }) => (
    <section className="p-grid p-fluid">
        <div className="position-tag p-col-12 p-sm-12 p-md-3 p-lg-2 p-mt-2 small-margin-top-0 small-padding-bottom-0">{sectionName}</div>
        <div className="position-controls p-col-12 p-sm-12 p-md-9 p-lg-9 no-pad-left no-pad-right">{children}</div>
    </section>
)

const ViewportNameSection = ({
    order,
    inputName,
    label,
    inputValue,
    handleChange,
    bgColorClass = 'light-green-bg',
    idx
}) => {
    return (
        <div className="adapter-parameter-values desk" data-index="0" style={{ order }}>
            <div className={`p-d-none sm-pad small-show dark-desktop`}>
                <h6 className="bold text-center capitalize mb-0 text-black">{label}</h6>
            </div>
            <AdpositionInput
                bgColorClass={bgColorClass}
                label=""
                name={inputName}
                value={inputValue}
                showCheckBox={true}
                handleChange={handleChange}
                idx={idx} />
        </div>

    )
}


/**
 * 
 * @param {Object} param0 
 * @param {Array<Object<string, {video: VideoAdPositionConfig, display: Object}>>} param0.inputs
 * @param {(idx: number, rule: string, component: React.Component, positionName: string, type: "video" | "banner" | "other") => void} param0.handleChange
 * @param {[]} param0.filteredAdSizes
 * @param {() => void} param0.searchAdSizes
 * @param {(v) => void} param0.changeHandler
 * @param {(idx) => void} param0.deleteRowHandler
 */
function InputsRenderer({
    inputs,
    handleChange,
    filteredAdSizes = [],
    searchAdSizes = () => { },
    deleteRowHandler = () => { }
}) {
    return (
        <Accordion multiple>
            {
                inputs.map((item, idx) => {
                    let [[adPositionName, adPositionConfig]] = Object.entries(item)
                    let controls = []

                    for (let [rulename, value] of Object.entries({ ...DEFAULT_VIDEO_FIELDS, ...adPositionConfig.video })) {
                        let { label, component: Component, skipRender, options = [], defaultValue } = VIDEO_INPUTS[rulename] || {}

                        if (skipRender) {
                            continue
                        }

                        if (Component === InputNumber || Component === InputText) {
                            controls.push(
                                <InputSection sectionName={label}>
                                    <Component
                                        value={value || defaultValue}
                                        onChange={e => {
                                            handleChange(idx, rulename, Component, adPositionName, "video", e)
                                        }}
                                    />
                                </InputSection>
                            )
                        } else if (Component === Dropdown || Component === MultiSelect) {
                            let props = {}
                            if (Component === MultiSelect) {
                                props = {display: "chips"}
                            }
                            controls.push(
                                <InputSection sectionName={label}>
                                    <Component
                                        value={value || defaultValue}
                                        display="chip"
                                        options={options}
                                        onChange={e => {
                                            handleChange(idx, rulename, Component, adPositionName, "video", e)
                                        }}
                                    />
                                </InputSection>
                            )
                        }
                    }

                    let {
                        desk,
                        tab,
                        mob
                    } = adPositionConfig.banner || {}

                    return (
                        <AccordionTab
                            key={idx}
                            header={
                                <div
                                    css={css`
                                        display: flex;
                                        justify-content: space-between;     
                                        .position-name {
                                            text-transform: capitalize; 
                                        }
                                    `}
                                >
                                    <span className="position-name">{adPositionName || "n/a"}</span>
                                    <Button
                                        type="button"
                                        icon="pi pi-times"
                                        className="p-button-small p-button-danger p-button-rounded p-button-text p-button-icon-only remove-row-btn"
                                        onClick={(ev) => {
                                            ev.preventDefault()
                                            ev.stopPropagation()
                                            deleteRowHandler(idx)
                                        }}
                                    />
                                </div>
                            }
                        >

                            <InputSection sectionName="Ad Position Name">
                                <InputText
                                    value={adPositionName || ""}
                                    onChange={(ev) => {
                                        handleChange(
                                            idx,
                                            "description",
                                            InputText,
                                            adPositionName,
                                            "other",
                                            ev
                                        )
                                    }}
                                />
                            </InputSection>

                            {/* These are the video controls */}
                            {controls}
                            {/* End of video controls block */}

                            <div
                                className="adapter-parameters adpos"
                                css={css`
                                    .p-autocomplete-multiple-container.p-component {
                                        width: 100%; 
                                    }
                                `}
                            >
                                <div className="sizing-row all-sizes"></div>
                                <div className="controls-wrapper all-sizes">
                                    <div className="adapter-config">
                                        <div className="empty"></div>
                                        {["desktop", "tablet", "mobile"].map((dev, key) => (
                                            <div key={key} className={`device ${dev}`}>
                                                <h6 className="adapter-device-name">{dev}</h6>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="values display-ad">
                                        <div className="param-names">
                                            <p>
                                                <strong className="param">Viewport</strong>
                                            </p>
                                        </div>
                                        <ViewportNameSection
                                            label="Desktop Viewport"
                                            inputValue={desk.viewport || ''}
                                            handleChange={ev => {
                                                handleChange(
                                                    idx,
                                                    "desk.viewport",
                                                    InputText,
                                                    adPositionName,
                                                    "banner",
                                                    ev
                                                )
                                            }}
                                            idx={idx}
                                            order={0}
                                        />

                                        <ViewportNameSection
                                            label="Tablet Viewport"
                                            inputValue={tab.viewport || ''}
                                            handleChange={ev => {
                                                handleChange(
                                                    idx,
                                                    "tab.viewport",
                                                    InputText,
                                                    adPositionName,
                                                    "banner",
                                                    ev
                                                )
                                            }}
                                            idx={idx}
                                            order={2}
                                            bgColorClass="light-purple-bg"
                                        />

                                        <ViewportNameSection
                                            label="Mobile Viewport"
                                            inputValue={mob.viewport || ''}
                                            handleChange={ev => {
                                                handleChange(
                                                    idx,
                                                    "mob.viewport",
                                                    InputText,
                                                    adPositionName,
                                                    "banner",
                                                    ev
                                                )
                                            }}
                                            idx={idx}
                                            order={4}
                                            bgColorClass="light-yellow-bg"
                                        />

                                        <div className="param-names">
                                            <p>
                                                <strong className="param">Adsize</strong>
                                            </p>
                                        </div>
                                        <div className="adapter-parameter-values desk" data-index="0" style={{ order: -1 }}>
                                            <div className={`p-d-none sm-pad small-show dark-desktop`}>
                                                <h6 className="bold text-center capitalize mb-0 text-black">Desktop Adsize</h6>
                                            </div>
                                            <AdsizeAutocomplete
                                                bgColorClass="light-green-bg"
                                                name="deskAdsize"
                                                filteredAdSizes={filteredAdSizes}
                                                searchAdSizes={searchAdSizes}
                                                value={desk && desk.adsize ? desk.adsize.split(',') : ''}
                                                showCheckBox={true}
                                                handleSizeChange={ev => {
                                                    handleChange(
                                                        idx,
                                                        "desk.adsize",
                                                        "autocomplete",
                                                        adPositionName,
                                                        "banner",
                                                        ev
                                                    )
                                                }}
                                                idx={idx}
                                            />
                                        </div>
                                        <div className="adapter-parameter-values tab" data-index="1" style={{ order: 1 }}>
                                            <div className={`p-d-none sm-pad small-show dark-tablet`}>
                                                <h6 className="bold text-center capitalize mb-0 text-black">Tablet Adsize</h6>
                                            </div>
                                            <AdsizeAutocomplete
                                                bgColorClass="light-purple-bg"
                                                name="tabAdsize"
                                                filteredAdSizes={filteredAdSizes}
                                                searchAdSizes={searchAdSizes}
                                                value={tab && tab.adsize ? tab.adsize.split(',') : ''}
                                                showCheckBox={true}
                                                handleSizeChange={ev => {
                                                    handleChange(
                                                        idx,
                                                        "tab.adsize",
                                                        "autocomplete",
                                                        adPositionName,
                                                        "banner",
                                                        ev
                                                    )
                                                }}
                                                idx={idx}
                                            />
                                        </div>
                                        <div className="adapter-parameter-values mob" data-index="2" style={{ order: 3 }}>
                                            <div className={`p-d-none sm-pad small-show dark-mobile`}>
                                                <h6 className="bold text-center capitalize mb-0 text-black">Mobile Adsize</h6>
                                            </div>
                                            <AdsizeAutocomplete
                                                bgColorClass="light-yellow-bg"
                                                name="mobAdsize"
                                                filteredAdSizes={filteredAdSizes}
                                                searchAdSizes={searchAdSizes}
                                                value={mob && mob.adsize ? mob.adsize.split(',') : ''}
                                                showCheckBox={true}
                                                handleSizeChange={ev => {
                                                    handleChange(
                                                        idx,
                                                        "mob.adsize",
                                                        "autocomplete",
                                                        adPositionName,
                                                        "banner",
                                                        ev
                                                    )
                                                }}
                                                idx={idx}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </AccordionTab>
                    )
                })
            }
        </Accordion>
    )
}


const SyndicationAdPositions = () => {
    const [adSizesAvailable, setAvailableAdSizes] = useState(ADSIZE_SUGGESTIONS);
    const [filteredAdSizes, setFilteredAdSizes] = useState([]);
    const [multiFieldRetrieveMutation] = useMutation(MULTI_FIELD_READ_MUTATION)
    const [multiFieldUpdateMutation] = useMutation(MULTI_FIELD_UPDATE_MUTATION)
    const { currentDomain } = useSelector((state) => state.userInformation);
    const [syndicationAdPositions, setSyndicationAdPositions] = useState([])
    const dispatch = useDispatch()

    /**
     * Converts an array of single-key objects into a single object.
     * Handles key collisions by appending _2, _3, etc.
     *
     * @param {Array<Object>} data
     * @returns {Object}
     */
    function arrayToObjectWithCollisionHandling(data) {
        if (!data.length) {
            return []
        }

        return data.reduce((acc, item) => {
            const [key, value] = Object.entries(item)[0];

            let finalKey = key;
            let counter = 2;

            while (Object.prototype.hasOwnProperty.call(acc, finalKey)) {
                finalKey = `${key}_${counter}`;
                counter++;
            }

            acc[finalKey.toLowerCase().replaceAll(" ", "_")] = value;
            return acc;
        }, {});
    }

    /**
     * Converts an object into an array of single-key objects.
     *
     * @param {Object} data
     * @returns {Array<Object>}
     */
    function objectToArray(data) {
        if (!Object.entries(data).length) {
            return []
        }
        return Object.entries(data).map(([key, value]) => ({
            [key]: value
        }));
    }


    useEffect(() => {
        if (currentDomain) {
            multiFieldRetrieveMutation({
                variables: {
                    fieldDefinitions: JSON.stringify([
                        { field_name: 'syndication_ad_positions', post_id: currentDomain, parse: true }
                    ])
                }
            }).then(response => {
                let {
                    data: { multiFieldRetrieveMutation: { response: syndicationPosData } }
                } = response

                syndicationPosData = JSON.parse(syndicationPosData) || {}
                let finalAdPositions = Object.create(null)
                finalAdPositions.syndication_ad_positions = Object.create(null)

                for (let [positionName, positionData] of Object.entries(syndicationPosData.syndication_ad_positions || {})) {
                    positionData.video = { ...DEFAULT_VIDEO_FIELDS, ...(positionData.video || {}) }
                    finalAdPositions.syndication_ad_positions[positionName.toLowerCase().replaceAll('_', ' ')] = positionData 
                }

                let arrayShapedData = objectToArray(finalAdPositions.syndication_ad_positions)
                setSyndicationAdPositions(arrayShapedData)

            }).catch(err => {
                console.log(err)
            })
        } else {
            setSyndicationAdPositions([])
        }
    }, [currentDomain])

    const searchAdSizes = (event) => {
        let filteredSizes = null;
        let query = event.query.trim();
        if (!query.length) {
            filteredSizes = [...adSizesAvailable];
        } else if (query.match(/^\d+x\d+$/)) {
            setAvailableAdSizes([...adSizesAvailable, query]);
            filteredSizes = [...adSizesAvailable.filter((l) => l.includes(query)), query];
        } else {
            filteredSizes = [...adSizesAvailable].filter((list) => {
                return list.includes(event.query);
            });
        }
        setFilteredAdSizes(filteredSizes);
    };


    /**
     * @param {number} ruleIndex
     * @param {string} rule
     * @param {React.Component | "autocomplete"} component
     * @param {string} positionName
     * @param {"video" | "banner" | "other"} type
     * @param {*} value
     */
    function handleChange(ruleIndex, rule, component, positionName, type, event) {
        if (!component) {
            return
        }

        setSyndicationAdPositions(
            syndicationAdPositions.map((position, idx) => {
                if (ruleIndex !== idx) {
                    return position
                }

                const config = position[positionName]

                // Renaming the ad position itself swaps the object's key
                if ((component === InputNumber || component === InputText) && type === 'other') {
                    return { [event.target.value]: config }
                }

                const typeConfig = config[type] || {}

                if (type === 'banner') {
                    // Banner edits target a nested device object (desk/tab/mob)
                    let [device, label] = rule.split('.')
                    let value = component === 'autocomplete'
                        ? event.target.value.join(',')
                        : event.target.value
                    return {
                        [positionName]: {
                            ...config,
                            [type]: {
                                ...typeConfig,
                                [device]: {
                                    ...(typeConfig[device] || {}),
                                    [label]: value
                                }
                            }
                        }
                    }
                }

                // Flat field (video)
                let value
                if (component === Dropdown || component === MultiSelect) {
                    value = event.value
                } else if (component === InputNumber) {
                    value = event.value
                } else {
                    value = event.target.value
                }

                return {
                    [positionName]: {
                        ...config,
                        [type]: {
                            ...typeConfig,
                            [rule]: value
                        }
                    }
                }
            })
        )
    }

    return (
        <LayoutWrapper model={'domain'} renderOpsBar={true} saveCallback={async () => {
            let result = await multiFieldUpdateMutation({
                variables: {
                    rules: JSON.stringify([
                        {
                            field_name: 'syndication_ad_positions',
                            post_id: currentDomain,
                            value: JSON.stringify(arrayToObjectWithCollisionHandling(syndicationAdPositions))
                        }
                    ])
                }
            })
            result = JSON.parse(result.data.multiFieldUpdateMutation.response)
            if (result.error) {
                dispatch(pushMessage("Syndication ad positions", "There was an error trying to save your changes", "error"))
            } else {
                dispatch(pushMessage("Syndication ad positions", "The changes were saved", "success"))
            }
        }}>
            <InputsRenderer
                searchAdSizes={searchAdSizes}
                filteredAdSizes={filteredAdSizes}
                inputs={
                    syndicationAdPositions
                }
                handleChange={handleChange}
                deleteRowHandler={(idx) => {
                    setSyndicationAdPositions(
                        syndicationAdPositions.filter((__, i) => {
                            return idx !== i
                        })
                    )
                }}
            />
            <Cta
                ctaText="Add Position"
                icon={faPlusCircle}
                cssStyles={`
                    float: right;
                    margin-top: 1.5em;    
                `}
                onClick={ev => {
                    let count = syndicationAdPositions.length + 1
                    let defaultSyndicationAdPosition = {
                        ["Syndication Ad Position " + count]: {
                            video: {...DEFAULT_VIDEO_FIELDS, delivery: [...DEFAULT_VIDEO_FIELDS.delivery]},
                            banner: {
                                desk: {
                                    viewport: "Desktop viewport size",
                                    adsize: ""
                                },
                                tab: {
                                    viewport: "Tablet viewport size",
                                    adsize: ""
                                },
                                mob: {
                                    viewport: "Mobile viewport size",
                                    adsize: ""
                                }
                            }
                        }
                    }
                    setSyndicationAdPositions([...syndicationAdPositions, defaultSyndicationAdPosition])
                }}
            />
        </LayoutWrapper>
    )
}

export default SyndicationAdPositions